/**
 * Reading content files (named items, item definitions) field by field:
 * every problem is collected in one pass, so an editor can list them all.
 * Each reader returns the value when it is good, or undefined after noting
 * why it isn't.
 */

/** Every value of a string union, written as a record so the compiler flags a missing one. */
export function toUnionValues<T extends string>(values: Readonly<Record<T, true>>): readonly T[] {
    return Object.keys(values) as T[];
}

export type Fields = Readonly<Record<string, unknown>>;

export function isFields(value: unknown): value is Fields {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Collects problems while reading fields, so one pass reports all of them.
 * Each reader returns the value when it is good, or undefined after noting
 * why it isn't.
 */
export class FieldReader {
    readonly problems: string[] = [];

    text(fields: Fields, key: string, label: string): string | undefined {
        const value = fields[key];
        if (typeof value !== 'string' || value.trim() === '') {
            this.problems.push(`${label} is required.`);
            return undefined;
        }
        return value;
    }

    /** A string that may be empty (a description not written yet). */
    anyText(fields: Fields, key: string, label: string): string | undefined {
        const value = fields[key];
        if (typeof value !== 'string') {
            this.problems.push(`${label} must be text.`);
            return undefined;
        }
        return value;
    }

    /** Text that may be left out; when given, it must not be blank. */
    optionalText(fields: Fields, key: string, label: string): string | undefined {
        return fields[key] === undefined ? undefined : this.text(fields, key, label);
    }

    optionalBoolean(fields: Fields, key: string, label: string): boolean | undefined {
        const value = fields[key];
        if (value === undefined) return undefined;
        if (typeof value !== 'boolean') {
            this.problems.push(`${label} must be true or false.`);
            return undefined;
        }
        return value;
    }

    number(fields: Fields, key: string, label: string, range: { readonly min?: number; readonly max?: number; readonly isInteger?: boolean } = {}): number | undefined {
        const value = fields[key];
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            this.problems.push(`${label} must be a number.`);
            return undefined;
        }
        if (range.isInteger && !Number.isInteger(value)) {
            this.problems.push(`${label} must be a whole number.`);
            return undefined;
        }
        if ((range.min !== undefined && value < range.min) || (range.max !== undefined && value > range.max)) {
            this.problems.push(`${label} must be between ${range.min ?? '-inf'} and ${range.max ?? 'inf'}.`);
            return undefined;
        }
        return value;
    }

    optionalNumber(fields: Fields, key: string, label: string, range: { readonly min?: number; readonly max?: number; readonly isInteger?: boolean } = {}): number | undefined {
        return fields[key] === undefined ? undefined : this.number(fields, key, label, range);
    }

    oneOf<T extends string>(fields: Fields, key: string, label: string, values: readonly T[]): T | undefined {
        const value = fields[key];
        if (typeof value !== 'string' || !(values as readonly string[]).includes(value)) {
            this.problems.push(`${label} must be one of: ${values.join(', ')}.`);
            return undefined;
        }
        return value as T;
    }

    fields(value: unknown, label: string): Fields | undefined {
        if (!isFields(value)) {
            this.problems.push(`${label} must be an object.`);
            return undefined;
        }
        return value;
    }
}

